import { apiClient } from '@/api/client';
import type { PaginatedResponse } from '@/lib/types/api.types';
import type {
  ResourceItemDto,
  ResourceItemDetailDto,
  ResourceFolderDto,
  ResourceFolderDetailDto,
} from '@/lib/types/resources.types';

/**
 * Member-facing resource reads (MYN-100).
 *
 * All paths are relative — the `/v1` prefix is carried by `API_BASE_URL` in the
 * axios client, matching the events and profile API clients.
 */

/**
 * Fetch the featured resource for the member landing page.
 *
 * Asks the API for the featured row specifically rather than downloading every
 * item and scanning for `isFeatured`: the endpoint is paginated, so a scan
 * would silently lose the featured card once the catalogue outgrew one page.
 *
 * @returns a single-element list when a featured item exists, else empty.
 */
export async function getFeaturedResourceItems(): Promise<ResourceItemDto[]> {
  const res = await apiClient.get<PaginatedResponse<ResourceItemDto>>('/resources/items', {
    params: { isFeatured: 'true', pageSize: '1' },
  });
  return res.data?.data ?? [];
}

/**
 * The newest resource items, for the member dashboard's "Resources for you"
 * (MYN-254). Same member list endpoint as the resources page, sorted newest
 * first and capped, so hidden items stay excluded server-side.
 */
export async function getLatestResourceItems(
  limit: number,
): Promise<{ items: ResourceItemDto[]; total: number }> {
  const res = await apiClient.get<PaginatedResponse<ResourceItemDto>>('/resources/items', {
    params: { sort: '-createdAt', pageSize: String(limit) },
  });
  return { items: res.data?.data ?? [], total: res.data?.meta?.total ?? 0 };
}

/**
 * Fetch one resource item's full detail, including a short-lived `downloadUrl`
 * for file-type resources.
 *
 * @param id - the resource item UUID
 * @returns the detail DTO including `downloadUrl` when the item has a file.
 */
export async function getResourceItemDetail(id: string): Promise<ResourceItemDetailDto> {
  const res = await apiClient.get<ResourceItemDetailDto>(`/resources/items/${id}`);
  return res.data;
}

/**
 * Fetch the active (non-retired) resource folders for the member landing page.
 *
 * @returns active folders in display order.
 */
export async function getResourceFolders(): Promise<ResourceFolderDto[]> {
  const res = await apiClient.get<ResourceFolderDto[]>('/resources/folders/active');
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Fetch one folder by slug, including its resource items.
 *
 * @param slug - the folder URL slug
 * @returns the folder detail DTO with nested `items[]`.
 */
export async function getResourceFolderBySlug(slug: string): Promise<ResourceFolderDetailDto> {
  const res = await apiClient.get<ResourceFolderDetailDto>(`/resources/folders/${slug}`);
  return res.data;
}
